import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import "./Dashboard.css";
import { Context } from "../../utils/context";
import { findAll } from "../../utils/apis/dashboard/dashboard";
import { Link } from "react-router-dom";
import { formatAmount } from "../../utils/common";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  FiUsers,
  FiUserX,
  FiUserCheck,
  FiCreditCard,
  FiDatabase,
  FiArrowDownLeft,
  FiArrowUpRight,
  FiPercent,
  FiTrendingUp,
  FiTrendingDown,
  FiRefreshCw,
  FiBriefcase,
  FiChevronRight,
  FiActivity,
  FiClock,
  FiZap,
} from "react-icons/fi";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
);

// Rummy Samrat logo palette: maroon + gold
const COLORS = {
  red: "#ff5a5f",
  green: "#4ade80",
  gold: "#e8b923",
  blue: "#ffd98a",
  violet: "#f59e5b",
  muted: "#d8b878",
  grid: "rgba(232, 185, 35, 0.1)",
};

const formatCount = (n) =>
  new Intl.NumberFormat("en-IN").format(Math.round(Number(n) || 0));

const compactINR = (v) =>
  "₹" +
  new Intl.NumberFormat("en-IN", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(v);

const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

// Animates a number from its previous value to `target`
function useCountUp(target, duration = 1600) {
  const [value, setValue] = useState(0);
  const fromRef = useRef(0);

  useEffect(() => {
    const from = fromRef.current;
    const to = Number(target) || 0;
    let frame;
    let start;

    const step = (ts) => {
      if (start === undefined) start = ts;
      const t = Math.min((ts - start) / duration, 1);
      const current = from + (to - from) * easeOutExpo(t);
      setValue(current);
      fromRef.current = current;
      if (t < 1) frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);

  return value;
}

function AnimatedNumber({ value, format = formatCount, duration }) {
  const current = useCountUp(value, duration);
  return <>{format(current)}</>;
}

// Sets the cursor position as CSS vars for the spotlight effect
const trackSpotlight = (e) => {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`);
};

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function greeting(hour) {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function KpiCard({
  label,
  value,
  format,
  icon: Icon,
  tone,
  to,
  state,
  hint,
  loading,
  index,
}) {
  return (
    <Link
      to={to}
      state={state}
      className={`dp-card dp-kpi dp-reveal tone-${tone}`}
      style={{ "--i": index }}
      onMouseMove={trackSpotlight}>
      <div className="dp-kpi-top">
        <span className="dp-icon">
          <Icon />
        </span>
        <FiChevronRight className="dp-arrow" />
      </div>
      <span className="dp-kpi-label">{label}</span>
      {loading ? (
        <div className="dp-skeleton dp-skeleton-lg" />
      ) : (
        <div className="dp-kpi-value">
          <AnimatedNumber value={value} format={format} />
        </div>
      )}
      <div className="dp-kpi-foot">{hint}</div>
    </Link>
  );
}

function ProgressRow({
  label,
  value,
  max,
  icon: Icon,
  tone,
  to,
  loading,
  ready,
}) {
  const pct = max > 0 ? Math.max((value / max) * 100, value > 0 ? 3 : 0) : 0;
  return (
    <Link to={to} className={`dp-row tone-${tone}`}>
      <span className="dp-icon dp-icon-sm">
        <Icon />
      </span>
      <div className="dp-row-body">
        <div className="dp-row-line">
          <span className="dp-row-label">{label}</span>
          {loading ? (
            <span className="dp-skeleton dp-skeleton-sm" />
          ) : (
            <span className="dp-row-value">
              <AnimatedNumber value={value} format={formatAmount} />
            </span>
          )}
        </div>
        <div className="dp-track">
          <span
            className="dp-fill"
            style={{ width: ready ? `${pct}%` : "0%" }}
          />
        </div>
      </div>
    </Link>
  );
}

function Ring({ pct, tone, label, sub, ready }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  const clamped = Math.min(Math.max(pct, 0), 100);
  return (
    <div className={`dp-ring tone-${tone}`}>
      <svg viewBox="0 0 120 120" aria-hidden="true">
        <circle className="dp-ring-bg" cx="60" cy="60" r={r} />
        <circle
          className="dp-ring-fg"
          cx="60"
          cy="60"
          r={r}
          strokeDasharray={c}
          strokeDashoffset={ready ? c * (1 - clamped / 100) : c}
        />
      </svg>
      <div className="dp-ring-center">
        <strong>
          <AnimatedNumber
            value={ready ? pct : 0}
            format={(v) => `${v.toFixed(1)}%`}
          />
        </strong>
      </div>
      <div className="dp-ring-text">
        <span>{label}</span>
        <small>{sub}</small>
      </div>
    </div>
  );
}

const verticalGradient = (from, to) => (ctx) => {
  const { chart } = ctx;
  const { ctx: c, chartArea } = chart;
  if (!chartArea) return from;
  const g = c.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
  g.addColorStop(0, to);
  g.addColorStop(1, from);
  return g;
};

// Draws each bar's exact amount above it, so tiny values stay readable
const barValueLabels = {
  id: "dpBarValueLabels",
  afterDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    const meta = chart.getDatasetMeta(0);
    const values = chart.data.datasets[0]?.rawValues || [];
    ctx.save();
    ctx.font = "600 12px Inter, Roboto, sans-serif";
    ctx.fillStyle = "#fff4d6";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    meta.data.forEach((bar, i) => {
      const y = bar.skip ? chartArea.bottom : bar.y;
      ctx.fillText(formatAmount(values[i]), bar.x, y - 6);
    });
    ctx.restore();
  },
};

function Dashboard() {
  const { UnauthorizedLogOut, userdata } = useContext(Context);

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const now = useClock();

  const getDataAll = useCallback(async () => {
    setLoading(true);
    try {
      const response = await findAll();
      setData(response?.data?.data || {});
      setLastUpdated(new Date());
    } catch (error) {
      console.error(error.message);
      await UnauthorizedLogOut(error);
    } finally {
      setLoading(false);
    }
  }, [UnauthorizedLogOut]);

  useEffect(() => {
    getDataAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Delay bar/ring fills one frame so the CSS transition plays
  useEffect(() => {
    if (loading) return;
    const id = setTimeout(() => setReady(true), 80);
    return () => clearTimeout(id);
  }, [loading]);

  const today = new Date().toISOString().split("T")[0];
  const dateLabel = now.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const timeLabel = now.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const totalUsers = Number(data?.total_users) || 0;
  const inactiveUsers = Number(data?.total_inactive_users) || 0;
  const activeUsers = Math.max(totalUsers - inactiveUsers, 0);
  const activePct = totalUsers ? (activeUsers / totalUsers) * 100 : 0;

  const totalWallet = Number(data?.total_wallet_amount) || 0;
  const totalDeposits = Number(data?.total_deposite_money) || 0;
  const totalWithdraws = Number(data?.total_withdraw_money) || 0;
  const totalCommission = Number(data?.total_commission) || 0;
  const netFlow = totalDeposits - totalWithdraws;

  const todayDeposits = Number(data?.todays_deposite_money) || 0;
  const todayWithdraws = Number(data?.todays_withdraw_money) || 0;
  const todayCommission = Number(data?.todays_commission) || 0;
  const todayNet = todayDeposits - todayWithdraws;
  const todayFlow = todayDeposits + todayWithdraws;
  const todayInPct = todayFlow ? (todayDeposits / todayFlow) * 100 : 50;

  // History filters that list exactly the rows behind the totals above:
  // accepted online + cash deposits, accepted withdraw requests / admin
  // removals + cash withdraws (game gift rows share the withdraw type)
  const depositLink = "wallet_type=3,9&type=Credit&request_status=2";
  const withdrawLink =
    "wallet_type=1,10&type=Debit&request_status=2&exclude_game=1";

  const pendingDeposits = data?.deposit?.deposit?.pending;
  const pendingWithdraws = data?.withdraw?.online?.pending;

  const payoutRatio = totalDeposits
    ? (totalWithdraws / totalDeposits) * 100
    : 0;
  const commissionYield = totalDeposits
    ? (totalCommission / totalDeposits) * 100
    : 0;

  // Role-specific balances, only present for some users
  const extraBalances = [
    { key: "total_agency_balance", label: "Justbooking Balance" },
    { key: "agents_wallet", label: "Agents Wallet Balance" },
    { key: "customers_wallet", label: "Customers Wallet Balance" },
  ]
    .filter((b) => data?.[b.key])
    .map((b) => ({
      label: b.label,
      value: Number(data[b.key]) || 0,
      format: formatAmount,
      icon: FiBriefcase,
      tone: "gold",
      to: "/dashboard",
      hint: "Current balance",
    }));

  const kpis = [
    ...extraBalances,
    {
      label: "Total Customers",
      value: totalUsers,
      format: formatCount,
      icon: FiUsers,
      tone: "blue",
      to: "/agent-management/customer",
      hint: `${formatCount(activeUsers)} active · ${formatCount(
        inactiveUsers,
      )} inactive`,
    },
    {
      label: "My Wallet",
      value: Number(data?.my_wallet_amount) || 0,
      format: formatAmount,
      icon: FiCreditCard,
      tone: "gold",
      to: "/wallet/history",
      state: { name: userdata?.name },
      hint: "Your available balance",
    },
    {
      label: "Total Commission",
      value: totalCommission,
      format: formatAmount,
      icon: FiPercent,
      tone: "violet",
      to: "/wallet/history?wallet_type=2&type=Credit",
      hint: `${formatAmount(todayCommission)} earned today`,
    },
    {
      label: "Net Flow",
      value: netFlow,
      format: formatAmount,
      icon: netFlow >= 0 ? FiTrendingUp : FiTrendingDown,
      tone: netFlow >= 0 ? "green" : "red",
      to: "/wallet/history",
      hint: "All-time deposits − withdrawals",
    },
    {
      label: "Pending Deposits",
      value: Number(pendingDeposits?.total_amount) || 0,
      format: formatAmount,
      icon: FiClock,
      tone: "blue",
      to: "/wallet/deposit-request",
      hint: `${formatCount(
        Number(pendingDeposits?.total_count) || 0,
      )} waiting for approval`,
    },
    {
      label: "Pending Withdrawals",
      value: Number(pendingWithdraws?.total_amount) || 0,
      format: formatAmount,
      icon: FiClock,
      tone: "red",
      to: "/wallet/request",
      hint: `${formatCount(
        Number(pendingWithdraws?.total_count) || 0,
      )} on hold, not yet paid out`,
    },
  ];

  const todayRows = [
    {
      label: "Deposits",
      value: todayDeposits,
      icon: FiArrowDownLeft,
      tone: "green",
      to: `/wallet/history?${depositLink}&date=${today}`,
    },
    {
      label: "Withdrawals",
      value: todayWithdraws,
      icon: FiArrowUpRight,
      tone: "red",
      to: `/wallet/history?${withdrawLink}&date=${today}`,
    },
    {
      label: "Commission",
      value: todayCommission,
      icon: FiPercent,
      tone: "gold",
      to: `/wallet/history?wallet_type=2&date=${today}&type=Credit`,
    },
  ];
  const todayMax = Math.max(...todayRows.map((r) => r.value));

  const allTimeRows = [
    {
      label: "Deposits",
      value: totalDeposits,
      icon: FiArrowDownLeft,
      tone: "green",
      to: `/wallet/history?${depositLink}`,
    },
    {
      label: "Withdrawals",
      value: totalWithdraws,
      icon: FiArrowUpRight,
      tone: "red",
      to: `/wallet/history?${withdrawLink}`,
    },
    {
      label: "Commission",
      value: totalCommission,
      icon: FiPercent,
      tone: "gold",
      to: "/wallet/history?wallet_type=2&type=Credit",
    },
  ];
  const allTimeMax = Math.max(...allTimeRows.map((r) => r.value));

  const barValues = [todayDeposits, todayWithdraws, todayCommission];
  const barChart = {
    labels: ["Deposits", "Withdrawals", "Commission"],
    datasets: [
      {
        label: "Today",
        // Zero becomes null so it draws no bar; small non-zero amounts
        // get minBarLength so they stay visible next to large ones
        data: barValues.map((v) => v || null),
        rawValues: barValues,
        minBarLength: 8,
        backgroundColor: [
          verticalGradient(COLORS.green, "rgba(74,222,128,0.3)"),
          verticalGradient(COLORS.red, "rgba(255,90,95,0.3)"),
          verticalGradient("#fff1a8", "rgba(156,106,18,0.5)"),
        ],
        hoverBackgroundColor: [COLORS.green, COLORS.red, COLORS.gold],
        borderWidth: 0,
        borderRadius: 6,
        borderSkipped: "bottom",
        maxBarThickness: 56,
      },
    ],
  };

  const tooltipStyle = {
    backgroundColor: "#2a0507",
    borderColor: "#e8b923",
    borderWidth: 1,
    padding: 10,
    cornerRadius: 8,
    titleColor: "#fff1a8",
    bodyColor: "#fff4d6",
    displayColors: false,
  };

  const barOptions = {
    maintainAspectRatio: false,
    layout: { padding: { top: 24 } },
    animation: {
      duration: 1400,
      easing: "easeOutQuart",
      delay: (ctx) =>
        ctx.type === "data" && ctx.mode === "default" ? ctx.dataIndex * 180 : 0,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tooltipStyle,
        callbacks: { label: (ctx) => formatAmount(ctx.raw) },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: COLORS.muted, font: { size: 12, weight: 600 } },
      },
      y: {
        grid: { color: COLORS.grid },
        border: { display: false },
        ticks: {
          color: COLORS.muted,
          font: { size: 11 },
          callback: compactINR,
          maxTicksLimit: 5,
        },
      },
    },
  };

  const donutChart = {
    labels: ["Active", "Inactive"],
    datasets: [
      {
        data: [activeUsers, inactiveUsers],
        backgroundColor: [COLORS.gold, "#5a0b10"],
        hoverBackgroundColor: ["#fff1a8", "#7a1218"],
        borderWidth: 0,
        borderRadius: 4,
        spacing: 3,
      },
    ],
  };

  const donutOptions = {
    cutout: "80%",
    maintainAspectRatio: false,
    animation: { animateRotate: true, duration: 1600, easing: "easeOutQuart" },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tooltipStyle,
        callbacks: { label: (ctx) => `${ctx.label}: ${formatCount(ctx.raw)}` },
      },
    },
  };

  return (
    <section className="dash-pro">
      <div className="dp-content">
        <header className="dp-header dp-reveal" style={{ "--i": 0 }}>
          <div>
            <h1 className="dp-title">
              <span className="dp-title-text">♛ Dashboard</span>
              <span className="dp-live">
                <span className="dp-live-dot" /> Live
              </span>
            </h1>
            <p className="dp-subtitle">
              {greeting(now.getHours())}
              {userdata?.name ? `, ${userdata.name}` : ""}. Here's how the
              platform is performing today.
            </p>
          </div>
          <div className="dp-header-actions">
            <div className="dp-clock">
              <FiClock />
              <div>
                <strong>{timeLabel}</strong>
                <span>{dateLabel}</span>
              </div>
            </div>
            <button
              type="button"
              className="dp-refresh"
              onClick={getDataAll}
              disabled={loading}
              title={
                lastUpdated
                  ? `Last updated ${lastUpdated.toLocaleTimeString("en-IN")}`
                  : "Refresh"
              }>
              <FiRefreshCw className={loading ? "dp-spin" : ""} />
              <span>{loading ? "Syncing" : "Refresh"}</span>
            </button>
          </div>
        </header>

        <div className="dp-hero-grid">
          <Link
            to="/wallet/history"
            className="dp-hero dp-reveal"
            style={{ "--i": 1 }}
            onMouseMove={trackSpotlight}>
            <div className="dp-hero-inner">
              <div className="dp-hero-top">
                <span className="dp-hero-label">
                  <span className="dp-icon tone-gold">
                    <FiDatabase />
                  </span>
                  Total Wallet Pool
                </span>
                <span
                  className={`dp-badge ${
                    todayNet >= 0 ? "tone-green" : "tone-red"
                  }`}>
                  {todayNet >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
                  {formatAmount(todayNet)} today
                </span>
              </div>

              {loading ? (
                <div className="dp-skeleton dp-skeleton-xl" />
              ) : (
                <div className="dp-hero-value">
                  <AnimatedNumber
                    value={totalWallet}
                    format={formatAmount}
                    duration={2200}
                  />
                </div>
              )}

              <div className="dp-flow">
                <div className="dp-flow-head">
                  <span>
                    <i className="dot green" /> Inflow{" "}
                    <b>{formatAmount(todayDeposits)}</b>
                  </span>
                  <span>
                    Outflow <b>{formatAmount(todayWithdraws)}</b>{" "}
                    <i className="dot red" />
                  </span>
                </div>
                <div className="dp-flow-bar">
                  <span
                    className="in"
                    style={{ width: ready ? `${todayInPct}%` : "50%" }}
                  />
                  <span className="out" />
                </div>
                <small>Today's money movement split</small>
              </div>
            </div>
          </Link>

          <div className="dp-card dp-health dp-reveal" style={{ "--i": 2 }}>
            <div className="dp-panel-head">
              <h2>
                <FiActivity /> Platform Health
              </h2>
            </div>
            <div className="dp-rings">
              <Ring
                pct={payoutRatio}
                tone="red"
                label="Payout ratio"
                sub="Withdrawals ÷ deposits"
                ready={ready}
              />
              <Ring
                pct={commissionYield}
                tone="gold"
                label="Commission yield"
                sub="Commission ÷ deposits"
                ready={ready}
              />
              <Ring
                pct={activePct}
                tone="blue"
                label="Active players"
                sub="Active ÷ total customers"
                ready={ready}
              />
            </div>
          </div>
        </div>

        <div className="dp-kpis">
          {kpis.map((kpi, i) => (
            <KpiCard key={kpi.label} {...kpi} index={i + 3} loading={loading} />
          ))}
        </div>

        <div className="dp-split">
          <div
            className="dp-card dp-panel dp-reveal"
            style={{ "--i": kpis.length + 3 }}>
            <div className="dp-panel-head">
              <h2>
                <FiZap /> Today's Activity
              </h2>
              <span className="dp-muted-chip">{dateLabel}</span>
            </div>
            <div className="dp-chart dp-chart-bar">
              {!loading && (
                <Bar
                  data={barChart}
                  options={barOptions}
                  plugins={[barValueLabels]}
                />
              )}
              {loading && <div className="dp-skeleton dp-skeleton-chart" />}
            </div>
            <div className="dp-rows">
              {todayRows.map((r) => (
                <ProgressRow
                  key={r.label}
                  {...r}
                  max={todayMax}
                  loading={loading}
                  ready={ready}
                />
              ))}
            </div>
          </div>

          <div className="dp-side">
            <div
              className="dp-card dp-panel dp-reveal"
              style={{ "--i": kpis.length + 4 }}>
              <div className="dp-panel-head">
                <h2>
                  <FiUsers /> Player Status
                </h2>
                <Link to="/agent-management/customer" className="dp-link">
                  View all <FiChevronRight />
                </Link>
              </div>
              <div className="dp-donut-wrap">
                <div className="dp-chart dp-chart-donut">
                  {!loading && (
                    <Doughnut data={donutChart} options={donutOptions} />
                  )}
                  <div className="dp-donut-center">
                    <strong>
                      <AnimatedNumber value={totalUsers} />
                    </strong>
                    <span>players</span>
                  </div>
                </div>
                <ul className="dp-legend">
                  <li>
                    <Link to="/agent-management/customer" className="tone-blue">
                      <FiUserCheck /> Active
                      <strong>
                        <AnimatedNumber value={activeUsers} />
                      </strong>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/agent-management/customer?status=0"
                      className="tone-muted">
                      <FiUserX /> Inactive
                      <strong>
                        <AnimatedNumber value={inactiveUsers} />
                      </strong>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            <div
              className="dp-card dp-panel dp-reveal"
              style={{ "--i": kpis.length + 5 }}>
              <div className="dp-panel-head">
                <h2>
                  <FiTrendingUp /> All-time Totals
                </h2>
              </div>
              <div className="dp-rows">
                {allTimeRows.map((r) => (
                  <ProgressRow
                    key={r.label}
                    {...r}
                    max={allTimeMax}
                    loading={loading}
                    ready={ready}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Dashboard;
